import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '@environments/environment';
import { AuthService } from '../../auth/services/auth.service';
import { TokenService } from '../../auth/services/token.service';

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private socket: Socket | null = null;
  private readonly connected$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly authService: AuthService,
    private readonly tokenService: TokenService,
  ) {}

  /**
   * Conecta al WebSocket server con autenticación del usuario actual
   */
  connect(): void {
    if (this.socket) {
      return;
    }

    const token = this.tokenService.getToken();
    console.log('token', token);
    if (!token) {
      console.error('No hay token disponible para conectar WebSocket');
      return;
    }

    // URL del WebSocket server desde configuración
    const wsUrl =
      environment.WEBSOCKET_URL ||
      (environment.production ? 'wss://your-api-domain.com' : 'ws://localhost:3000');

    // Obtener información del usuario desde el token
    const payload = this.tokenService.getPayload(token);
    console.log('payload', payload);

    this.socket = io(wsUrl, {
      auth: {
        token: token,
        userId: payload.usuarioId, // Ajusta según la estructura de tu JWT
        entidadId: payload.entidadId,
      },
      transports: ['websocket', 'polling'],
      autoConnect: false,
    });

    this.socket.on('connect', () => {
      console.log('WebSocket conectado');
      this.connected$.next(true);
    });

    this.socket.on('disconnect', () => {
      console.log('WebSocket desconectado');
      this.connected$.next(false);
    });

    this.socket.on('connect_error', (error: Error) => {
      console.error('Error de conexión WebSocket:', error);
      this.connected$.next(false);
    });

    this.socket.connect();
  }

  /**
   * Desconecta del WebSocket server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected$.next(false);
    }
  }

  /**
   * Verifica si está conectado
   */
  isConnected(): Observable<boolean> {
    return this.connected$.asObservable();
  }

  /**
   * Escucha eventos específicos del WebSocket
   */
  listen<T>(eventName: string): Observable<T> {
    return new Observable<T>((observer) => {
      const attach = () => {
        if (!this.socket) return;
        this.socket.on(eventName, (data: T) => observer.next(data));
      };

      if (this.socket) {
        attach();
      } else {
        const sub = this.connected$.subscribe((connected) => {
          if (connected) {
            sub.unsubscribe();
            attach();
          }
        });
      }

      return () => {
        if (this.socket) {
          this.socket.off(eventName);
        }
      };
    });
  }

  /**
   * Emite un evento al servidor
   */
  emit(eventName: string, data?: any): void {
    if (this.socket?.connected) {
      this.socket.emit(eventName, data);
    } else {
      console.warn('Intentando emitir evento sin conexión WebSocket activa');
    }
  }

  /**
   * Se auto-conecta cuando el usuario se autentica
   */
  autoConnect(): void {
    this.authService.currentUser$.subscribe((user) => {
      if (user) {
        this.connect();
      } else {
        this.disconnect();
      }
    });

    // También intentar conectar si ya hay un token válido
    if (this.tokenService.getToken() && this.authService.isLoggedIn()) {
      this.connect();
    }
  }
}
